import React, { useEffect, useState } from "react";
import api from "../../api/client";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, KeyRound, CheckCircle2, XCircle, UserMinus } from "lucide-react";

const EMPTY = {
  nome: "", email: "", password: "", ativo: true, access_profile: "equipe",
  can_see_valor_parcela: false, can_create_client: false, can_change_proposal_status: false,
};

const PROFILES = {
  equipe: { label: "Equipe completa", help: "Acesso aos módulos de clientes, propostas, treinamentos, operações e documentos." },
  comercial: { label: "Comercial", help: "Clientes e propostas. Sem acesso aos módulos técnicos e de treinamento." },
  treinamentos: { label: "Treinamentos", help: "Clientes, emissão, histórico e vencimentos de treinamentos." },
  operacional: { label: "Operacional", help: "Clientes, ações, visitas, obrigações e documentos técnicos." },
  leitura: { label: "Somente leitura", help: "Pode consultar todos os módulos, mas o servidor bloqueia qualquer alteração." },
};

export default function CollaboratorsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [pwdOnly, setPwdOnly] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/collaborators"); setItems(r.data); }
    catch (e) { toast.error("Falha ao carregar colaboradores"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setPwdOnly(false); setOpen(true); };
  const openEdit = (c) => {
    setForm({
      nome: c.nome, email: c.email, password: "", ativo: c.ativo,
      access_profile: c.access_profile || "equipe",
      can_see_valor_parcela: !!c.can_see_valor_parcela,
      can_create_client: !!c.can_create_client,
      can_change_proposal_status: !!c.can_change_proposal_status,
    });
    setEditingId(c.id); setPwdOnly(false); setOpen(true);
  };
  const openResetPwd = (c) => { setForm({ ...EMPTY, nome: c.nome, email: c.email, password: "" }); setEditingId(c.id); setPwdOnly(true); setOpen(true); };

  const save = async () => {
    if (!editingId) {
      if (!form.nome.trim() || !form.email.trim() || !form.password || form.password.length < 8) return toast.error("Preencha nome, e-mail e senha (mín. 8 caracteres)");
      try { await api.post("/admin/collaborators", form); toast.success("Colaborador criado"); setOpen(false); load(); }
      catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
      return;
    }

    const payload = {};
    if (pwdOnly) {
      if (!form.password || form.password.length < 8) return toast.error("Nova senha mín. 8 caracteres");
      payload.password = form.password;
    } else {
      if (form.nome) payload.nome = form.nome;
      if (form.email) payload.email = form.email;
      payload.ativo = form.ativo;
      payload.access_profile = form.access_profile || "equipe";
      payload.can_see_valor_parcela = !!form.can_see_valor_parcela;
      payload.can_create_client = !!form.can_create_client;
      payload.can_change_proposal_status = !!form.can_change_proposal_status;
      if (form.password) payload.password = form.password;
    }
    try { await api.put(`/admin/collaborators/${editingId}`, payload); toast.success(pwdOnly ? "Senha redefinida" : "Atualizado"); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };

  const deactivate = async () => {
    if (!confirmDeactivate) return;
    try {
      await api.delete(`/admin/collaborators/${confirmDeactivate.id}`);
      toast.success("Acesso desativado. O histórico foi preservado.");
      setConfirmDeactivate(null);
      load();
    } catch { toast.error("Falha ao desativar acesso"); }
  };

  const toggleAtivo = async (c) => {
    try { await api.put(`/admin/collaborators/${c.id}`, { ativo: !c.ativo }); load(); }
    catch { toast.error("Falha"); }
  };

  return (
    <div className="space-y-4" data-testid="tab-collaborators-content">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">Escolha um perfil simples para cada pessoa. O bloqueio é aplicado no servidor, não apenas no menu. Contas desativadas mantêm o histórico de auditoria.</p>
        <Button onClick={openNew} data-testid="collaborator-new-btn"><Plus className="mr-1 size-4" /> Novo colaborador</Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/30"><tr><th className="px-3 py-2 text-left label-tech">Nome</th><th className="px-3 py-2 text-left label-tech">E-mail</th><th className="px-3 py-2 text-center label-tech">Status</th><th className="px-3 py-2 text-left label-tech">Perfil</th><th className="px-3 py-2 text-left label-tech">Último login</th><th className="px-3 py-2 text-right label-tech">Ações</th></tr></thead>
          <tbody className="divide-y divide-border">
            {items.map((c) => (
              <tr key={c.id} data-testid={`collaborator-row-${c.id}`}>
                <td className="px-3 py-2 font-medium">{c.nome}</td>
                <td className="px-3 py-2 text-muted-foreground">{c.email}</td>
                <td className="px-3 py-2 text-center"><button onClick={() => toggleAtivo(c)} data-testid={`collaborator-toggle-${c.id}`} className="inline-flex items-center gap-1 text-xs">{c.ativo ? <Badge className="border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"><CheckCircle2 className="mr-1 size-3" /> Ativo</Badge> : <Badge variant="outline" className="text-muted-foreground"><XCircle className="mr-1 size-3" /> Inativo</Badge>}</button></td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1"><Badge variant="outline">{PROFILES[c.access_profile]?.label || "Equipe completa"}</Badge>{c.can_create_client && <Badge variant="outline" className="h-5 px-1.5 text-[9px]">+ cliente</Badge>}{c.can_change_proposal_status && <Badge variant="outline" className="h-5 px-1.5 text-[9px]">status</Badge>}{c.can_see_valor_parcela && <Badge variant="outline" className="h-5 px-1.5 text-[9px]">parcela</Badge>}</div>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{c.last_login ? new Date(c.last_login).toLocaleString("pt-BR") : "—"}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openResetPwd(c)} title="Redefinir senha"><KeyRound className="size-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(c)} title="Editar"><Pencil className="size-4" /></Button>
                  {c.ativo && <Button size="sm" variant="ghost" onClick={() => setConfirmDeactivate(c)} title="Desativar acesso"><UserMinus className="size-4" /></Button>}
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-sm text-muted-foreground">Nenhum colaborador cadastrado.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="collaborator-dialog">
          <DialogHeader><DialogTitle>{pwdOnly ? "Redefinir senha" : (editingId ? "Editar colaborador" : "Novo colaborador")}</DialogTitle><DialogDescription>{pwdOnly ? `Definir nova senha para ${form.email}` : "Defina o perfil conforme a atividade da pessoa. As permissões são verificadas também pela API."}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {!pwdOnly && (
              <>
                <div><Label>Nome *</Label><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
                <div><Label>E-mail *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                {editingId && <div className="flex items-center justify-between"><Label>Ativo</Label><Switch checked={form.ativo} onCheckedChange={(v) => setForm({ ...form, ativo: v })} /></div>}
                <div className="rounded-xl border border-border/70 bg-muted/10 p-3">
                  <Label htmlFor="access-profile">Perfil de acesso</Label>
                  <select id="access-profile" value={form.access_profile || "equipe"} onChange={(e) => setForm({ ...form, access_profile: e.target.value })} className="mt-2 h-10 w-full rounded-xl border border-input bg-background px-3 text-sm outline-none focus:border-primary" data-testid="collaborator-access-profile">
                    {Object.entries(PROFILES).map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}
                  </select>
                  <p className="mt-2 text-[11px] text-muted-foreground">{PROFILES[form.access_profile]?.help}</p>
                </div>

                <details className="rounded-xl border border-border/70 px-3 py-2">
                  <summary className="cursor-pointer text-sm font-medium">Ajustes extras</summary>
                  <div className="mt-3 space-y-3">
                    <div className="flex items-center justify-between gap-4"><div><Label htmlFor="perm-parcela">Ver valor da parcela mensal</Label><p className="text-[11px] text-muted-foreground">Se desligado, vê apenas o valor total permitido.</p></div><Switch id="perm-parcela" checked={!!form.can_see_valor_parcela} onCheckedChange={(v) => setForm({ ...form, can_see_valor_parcela: v })} /></div>
                    <div className="flex items-center justify-between gap-4"><div><Label htmlFor="perm-create-client">Cadastrar novos clientes</Label><p className="text-[11px] text-muted-foreground">Necessita também de perfil com acesso a Clientes.</p></div><Switch id="perm-create-client" checked={!!form.can_create_client} onCheckedChange={(v) => setForm({ ...form, can_create_client: v })} /></div>
                    <div className="flex items-center justify-between gap-4"><div><Label htmlFor="perm-prop-status">Mudar status de proposta</Label><p className="text-[11px] text-muted-foreground">Necessita também de perfil com acesso a Propostas.</p></div><Switch id="perm-prop-status" checked={!!form.can_change_proposal_status} onCheckedChange={(v) => setForm({ ...form, can_change_proposal_status: v })} /></div>
                  </div>
                </details>
              </>
            )}
            <div><Label>{editingId && !pwdOnly ? "Nova senha (opcional, mín. 8)" : "Senha * (mín. 8)"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} data-testid="collaborator-form-save">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDeactivate} onOpenChange={(openValue) => !openValue && setConfirmDeactivate(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Desativar acesso?</DialogTitle><DialogDescription><b>{confirmDeactivate?.nome}</b> não conseguirá mais entrar no sistema. O histórico de ações e auditoria será preservado.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmDeactivate(null)}>Cancelar</Button><Button variant="destructive" onClick={deactivate}><UserMinus className="size-4" /> Desativar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
